import React, { FC, memo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { NavList } from 'transfix-ui/components/NavList'
import { NavListFooter } from 'transfix-ui/components/NavListFooter'
import { NavListItem, NavListItemWrapper } from 'transfix-ui/components/NavListItem'
import {
  Box,
  makeStyles,
  Divider,
  IBoxProps as MuiBoxProps,
  createStyles,
  Typography,
} from 'transfix-ui/core'
import { avatarColors, brandColors } from 'transfix-ui/theme'
import { INavRouteGroup, INavRoute } from 'types'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { CollapseIcon, MenuIcon } from 'transfix-icons'
import { LogoIcon } from '../LogoIcon'
import { NavListFooterIndicator, INavListFooterIndicatorProps } from '../NavListFooterIndicator'
import navBG from './Nav-BG.png'

const SIDEBAR_WIDTH = 64
const SIDEBAR_WIDTH_EXPANDED = 266

const LOGO_WIDTH = 195
const LOGO_MAX_HEIGHT = 98.32

const NavListGroup: FC<Omit<MuiBoxProps, 'bgcolor'>> = ({ children, ...rest }) => {
  return (
    <Box data-test='nav-list-group' {...rest}>
      {children}
    </Box>
  )
}

interface ISideNavProps {
  /**
   * Toggles whether to show the nav as a line of icons
   * or a list of links with text
   */
  isNavExpanded?: boolean
  navRouteGroups: INavRouteGroup[]
  navFooterIndicator?: INavListFooterIndicatorProps
  /**
   * @example '/home'
   */
  homeRoute?: string
  onLogout?: () => void
  onSupport?: () => void
  onToggleNav?: () => void
  onClick?: () => void
  persistNavBar?: boolean
  /**
   * @example 'Copyright Transfix 2021'
   */
  footerText?: string
  /**
   * @example <FMSHeaderLogo logoStyle='black' />
   */
  logo?: React.ReactElement
}

type TClassKey =
  | 'root'
  | 'rootExpanded'
  | 'scrollWrapper'
  | 'logo'
  | 'logoExpanded'
  | 'divider'
  | 'logoWrapper'
  | 'toggleWrapper'
  | 'routeGroupWrapper'
  | 'envBox'

const useSideNavStyles = makeStyles(theme =>
  createStyles<TClassKey, { isNavExpanded?: boolean }>({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflowY: 'hidden',
      width: SIDEBAR_WIDTH,
      background: brandColors.coolGray7,
      backgroundImage: `url(${navBG})`,
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'bottom left',
      transition: `all 150ms ${theme.transitions.easing.sharp}`,
    },
    rootExpanded: {
      width: SIDEBAR_WIDTH_EXPANDED,
      transition: `all 150ms ${theme.transitions.easing.sharp}`,
    },
    scrollWrapper: {
      background: brandColors.transparent,
      flex: '1 1 auto',
      overflow: 'auto',
    },
    logoWrapper: {
      display: 'flex',
      padding: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 3 : 1),
      paddingTop: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 3.5 : 3),
      paddingBottom: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 2 : 1),
      backgroundColor: brandColors.transparent,
    },
    logo: {
      maxHeight: LOGO_MAX_HEIGHT,
      maxWidth: '100%',

      '& svg, & > a': {
        maxWidth: '100%',
        maxHeight: '100%',
        height: 'auto',

        // Removes default green line found around <a /> tags
        '&:focus': {
          outline: 'none',
          boxShadow: 'none',
        },
      },
    },
    logoExpanded: {
      height: 'auto',
      width: LOGO_WIDTH,
    },
    toggleWrapper: {
      backgroundColor: brandColors.transparent,
      padding: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 3 : 1),
      paddingTop: 0,
      paddingBottom: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 2 : 5),
    },
    divider: {
      borderColor: '#364146',
      opacity: 0.5,
      margin: ({ isNavExpanded }) => (isNavExpanded ? 0 : theme.spacing(1.5)),
    },
    envBox: {
      backgroundColor: brandColors.gold2,
      padding: '4px 12px 7px',
      border: `1px solid ${avatarColors.orange}`,
    },
    routeGroupWrapper: {
      backgroundColor: brandColors.transparent,
      padding: ({ isNavExpanded }) => theme.spacing(isNavExpanded ? 3 : 1),
    },
  })
)

/**
 * Used for app navigation
 */
export const SideNav = memo<ISideNavProps>(
  ({
    logo: logoProp,
    navRouteGroups = [],
    isNavExpanded,
    onToggleNav,
    onClick: onClickProp = () => {},
    homeRoute = '',
    navFooterIndicator,
  }) => {
    const { isMobile } = useBreakpointContext()
    const { userDetails } = useUserDetails()
    const { email, firstName, lastName } = userDetails || {}

    /**
     * Creates a click handler that combines general `onClick` prop
     * with any route specific `onClick` prop
     */
    const createClickHandler = useCallback(
      (route: INavRoute) => () => {
        onClickProp()
        const { onClick } = route || {}
        if (onClick) onClick()
      },
      [onClickProp]
    )

    const classes = useSideNavStyles({
      isNavExpanded,
    })

    return (
      <Box
        className={classNames(classes.root, { [classes.rootExpanded]: isNavExpanded })}
        data-test='side-nav'>
        <Box className={classes.scrollWrapper}>
          {/**
           * Logo
           */}
          <NavListGroup className={classes.logoWrapper}>
            <Box className={classNames(classes.logo, { [classes.logoExpanded]: isNavExpanded })}>
              <Link to={homeRoute} aria-label='side navigation logo' className='flex'>
                {isNavExpanded ? <>{logoProp}</> : <LogoIcon size='small' color='white' />}
              </Link>
              {__ENV__ !== 'production' && isNavExpanded && (
                <Box className={classes.envBox} mt={1}>
                  {__ENV__ === 'staging' ? (
                    <Typography variant='h3'>Staging</Typography>
                  ) : (
                    <Typography variant='h3'>Local</Typography>
                  )}
                </Box>
              )}
              {isNavExpanded && __STAGING_NEW__ && (
                <Box className={classes.envBox} mt={1}>
                  <Typography variant='h3'>Staging</Typography>
                </Box>
              )}
            </Box>
          </NavListGroup>
          {/**
           * Toggle Navigation
           */}
          <NavListGroup className={classes.toggleWrapper}>
            <NavList variant='dark'>
              <NavListItemWrapper name='Toggle Menu' isTooltipDisabled={isMobile || isNavExpanded}>
                <NavListItem
                  variant='dark'
                  text='Close Menu'
                  IconEl={isNavExpanded ? CollapseIcon : MenuIcon}
                  isTextVisible={isNavExpanded}
                  onClick={onToggleNav}
                  data-test='hamburger-button'
                />
              </NavListItemWrapper>
            </NavList>
          </NavListGroup>
          {/**
           * Routes
           */}
          {navRouteGroups.map(group => {
            const { name: groupName, keyName, routes = [], dividerBefore, dividerAfter } =
              group || {}
            return (
              <div key={`${groupName}-${keyName}`}>
                {dividerBefore && <Divider className={classes.divider} />}
                <NavListGroup className={classes.routeGroupWrapper}>
                  <NavList variant='dark' subheader={groupName} isSubheaderHidden={!isNavExpanded}>
                    {routes.map(route => {
                      const { IconEl, location, name, tag } = route || {}
                      const clickHandler = createClickHandler(route)
                      return (
                        <NavListItemWrapper
                          key={name}
                          name={name}
                          isTooltipDisabled={isMobile || isNavExpanded}>
                          <NavListItem
                            variant='dark'
                            location={location}
                            text={name}
                            IconEl={IconEl}
                            onClick={clickHandler}
                            isTextVisible={isNavExpanded}
                            tag={tag}
                          />
                        </NavListItemWrapper>
                      )
                    })}
                  </NavList>
                </NavListGroup>
                {dividerAfter && <Divider className={classes.divider} />}
              </div>
            )
          })}
        </Box>
        {navFooterIndicator?.icon && navFooterIndicator?.shouldShowIndicator && (
          <NavListFooterIndicator {...navFooterIndicator} />
        )}
        <NavListFooter user={{ email, firstName, lastName }} isNavExpanded={isNavExpanded} />
      </Box>
    )
  }
)

export default SideNav
